<template>
  <article v-if="article" class="flex flex-col">
    <div class="mb-[120px] flex gap-[20px]">
      <div class="flex max-w-[100%] flex-col xl:w-3/4 2xl:w-4/5">
        <h1
          v-if="isOldArticleFormat"
          class="mb-[28px] text-[32px] font-medium leading-[38px]"
          v-text="article.title"
        />
        <CommonBlocksImageArticleBlock
          v-if="isOldArticleFormat && article.image"
          class="mb-[32px]"
          is-social
          :data="article"
          :published-at="article.published_at"
          :src="article.image.src"
        />
        <div v-if="article?.json_body">
          <div
            class="flex justify-between text-[14px] leading-[18px] text-new-black-light"
          >
            <div
              class="flex cursor-pointer items-center px-4 py-2"
              @click="handleBookmark()"
            >
              <SvgIcon
                name="bookmark"
                class="mr-2 size-4"
                :class="{ 'text-ruby-red-100': isBookmarked }"
              ></SvgIcon>
              <div>В закладки</div>
            </div>
          </div>
          <EditorParser :json-str="article?.json_body">
            <EditorMainImage
              :image-src="getSrc"
              :title="article.title"
              :announce="article.announce"
              :source="article.cover_source"
              :description="article.cover_description"
              :time="article.time"
            />
          </EditorParser>
        </div>
        <div
          v-else
          ref="pageBodyRef"
          class="page-body mb-[65px]"
          v-html="article.body"
        />
        <!-- <CommonMobileBannerBlock /> -->
        <CommonBlocksNewTagsBlock
          v-if="tags"
          class="mb-[80px]"
          v-bind="{ tags }"
        />

        <CommonSocialsSocialRedesign
          v-if="article.id"
          :id="article.id"
          :model-type="article.model_type ?? 'article'"
          :views="article.views"
          class="mb-[80px]"
        />

        <ClientOnly>
          <div v-if="article.authors?.length" class="flex flex-col">
            <h3 class="mb-[36px] text-[24px] font-medium">Автор</h3>
            <div class="grid grid-flow-row gap-5">
              <CommonAuthorInfo
                v-for="author in article.authors"
                :key="author.id"
                :author="author"
              />
            </div>
          </div>
        </ClientOnly>
      </div>
      <CommonBlocksRightArticleBlock class="xl:w-1/4 2xl:w-1/5" />
    </div>

    <CommonBlocksPageFooterBlocks />
  </article>
</template>

<script setup>
import api from '@/services/api/api';

definePageMeta({
  layout: 'marginless',
});

const route = useRoute();
const toast = useToast();
const slug = route?.params?.slug;
const { articleSlug } = useArticleBySlug(slug);
const { isDesktop, isPad, isMobile } = useBreakpoint();

const article = computed(() => articleSlug?.data?.value);
const tags = computed(() => article.value?.tags);

const robots = ref('all');
const pageBodyRef = ref(null);

const isOldArticleFormat = computed(() => article.value?.json_body === null);

const getSrc = computed(() => {
  const src =
    article.value?.cover_image ||
    (isDesktop.value && article.value?.image?.desktop) ||
    (isPad.value && article.value?.image?.desktop) ||
    (isMobile.value && article.value?.image?.mobile) ||
    article.value?.image?.src;
  return src;
});

const seo = computed(() => article.value?.seo);
if (seo.value) {
  const arr = [];
  if (seo.value.index === false) arr.push('noindex');
  if (seo.value.follow === false) arr.push('nofollow');
  if (seo.value.archive === false) arr.push('noarchive');
  if (arr.length > 0) {
    robots.value = arr.reduce((previousValue, currentValue) => {
      return `${previousValue}, ${currentValue}`;
    });
  }
}

const isBookmarked = computed(() => article.value?.has_bookmark);

const handleBookmark = async () => {
  try {
    const { data: bookmark } = await api.socials.handleBookmark({
      model_type: article.value?.model_type,
      id: article.value?.id,
    });
    if (
      bookmark.err &&
      bookmark.err === 'You are not authorized or there are no such user'
    ) {
      toast.error('Необходимо авторизоваться!');
      return;
    }
    if (bookmark === 'Ok') {
      toast.success('Добавлено в избранное!');
    }
    if (bookmark === 'Deleted') {
      toast.success('Удалено из избранного!');
    }
    articleSlug.refetch();
  } catch (e) {
    toast.error(`${e}`);
  }
};

onMounted(() => {
  renderImageFooter(pageBodyRef.value);
});

useHead(
  computed(() => ({
    title: `${
      article.value?.title ? article.value?.title + ' |' : ''
    } Читать статьи по истории РФ для школьников и студентов`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: `Статьи о российской истории. ${
          seo.value?.metadescription ?? article.value?.title ?? ''
        }. Информация о Федеральном портале Истории России. Российское военно-историческое общество. Мы знаем правду.`,
      },
      {
        hid: 'keywords',
        name: 'keywords',
        content: `${article.value?.title ?? ''}`,
      },
      {
        'http-equiv': 'Last-Modified',
        content: new Date(seo.value?.last_modified).toUTCString(),
      },
      {
        hid: 'robots',
        name: 'robots',
        content: robots.value,
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: `https://histrf.ru${route.path}`,
      },
    ],
  })),
);
</script>
